import React, { useCallback, useMemo } from "react";

import Select from "antd/es/select";
import Tag from "antd/es/tag";
import cn from "classnames";
import { sortBy } from "lodash-es";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import {
  AvatarWithName,
  InlineSelectField,
  SelectField,
  SelectFieldProps,
} from "@mapmycustomers/ui";

import styles from "./UserMultiSelectField.module.scss";
import { getUserTag } from "./UserTag";

export interface UserMultiSelectFieldProps extends Omit<
  SelectFieldProps<Array<User["id"]>>,
  "filterOption" | "maxTagCount" | "mode" | "tagRender"
> {
  inline?: boolean;
  oneLiner?: boolean;
  users: User[];
}

const UserMultiSelectField = ({
  className,
  inline,
  oneLiner,
  placeholder,
  size,
  users,
  ...props
}: UserMultiSelectFieldProps) => {
  const intl = useIntl();

  const sortedUsers = useMemo(() => sortBy(users, [userDisplayName]), [users]);

  const SelectComponent = inline ? InlineSelectField<User["id"][]> : SelectField<User["id"][]>;

  const valueRender = useCallback(
    (userIds: null | undefined | User["id"][]) => {
      if (!userIds?.length) {
        return null;
      }
      const userMap = new Map(users.map((user) => [user.id, user]));
      return (
        <div className={styles.inlineValue}>
          {userIds
            .map((userId) => {
              const user = userMap.get(userId);
              return user ? (
                <Tag className={styles.tag} key={user.id}>
                  <AvatarWithName className={styles.avatar} user={user} />
                </Tag>
              ) : null;
            })
            .filter(isDefined)}
        </div>
      );
    },
    [users]
  );

  return (
    <SelectComponent
      {...props}
      className={cn(styles.container, className)}
      filterOption={defaultFilterOption}
      maxTagCount={oneLiner ? "responsive" : undefined}
      mode="multiple"
      placeholder={
        placeholder ??
        intl.formatMessage({
          id: "userMultiSelect.placeholder",
          defaultMessage: "Click or type to select users",
          description: "Placeholder displayed in a UserMultiSelectField component",
        })
      }
      size={size}
      tagRender={(props) => getUserTag({ ...props, size })}
      {...(inline ? { showFooterButtons: false, valueRender } : {})}
    >
      {sortedUsers.map((user) => (
        <Select.Option key={user.id} label={userDisplayName(user)} value={user.id}>
          <AvatarWithName className={styles.avatar} user={user} />
        </Select.Option>
      ))}
    </SelectComponent>
  );
};

export default UserMultiSelectField;
